import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PaperPlaneTilt,
  Image as ImageIcon,
  Translate,
  Sparkle,
  ChatCircleDots,
  CurrencyInr,
  Scales,
  Heart,
  ShieldCheck,
  Question,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const SUGGEST = [
  { icon: CurrencyInr, q: "Is today a good day to buy gold?" },
  { icon: Heart, q: "What jewellery suits my face shape?" },
  { icon: Scales, q: "Check if my gold price is fair" },
  { icon: ShieldCheck, q: "Explain this hallmark code to me" },
  { icon: Sparkle, q: "How much can I sell my old gold for?" },
  { icon: Question, q: "What is 916 BIS hallmark?" },
];

const LANGS = [
  { code: "auto", label: "Auto" },
  { code: "en", label: "English" },
  { code: "ta", label: "தமிழ்" },
  { code: "hi", label: "हिन्दी" },
  { code: "te", label: "తెలుగు" },
];

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [convId, setConvId] = useState(null);
  const [lang, setLang] = useState("auto");
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const pending = sessionStorage.getItem("mineai_pending_msg");
    if (pending) {
      sessionStorage.removeItem("mineai_pending_msg");
      send(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    const tempUser = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: msg,
    };
    setMessages((m) => [...m, tempUser]);
    setSending(true);
    try {
      const r = await api.post("/chat/send", {
        message: msg,
        conversation_id: convId,
        language: lang,
      });
      setConvId(r.data.conversation_id);
      setMessages((m) => [
        ...m.filter((x) => x.id !== tempUser.id),
        r.data.user_message,
        r.data.ai_message,
      ]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
      setMessages((m) => m.filter((x) => x.id !== tempUser.id));
    } finally {
      setSending(false);
    }
  };

  const empty = messages.length === 0;

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col" data-testid="chat-page">
      {/* Sub header */}
      <div className="border-b border-[#E8DFD1] bg-white/60 backdrop-blur">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="/mineai-logo.jpeg"
              alt="Mine AI"
              className="h-9 w-auto select-none"
              draggable="false"
            />
            <span className="hidden sm:inline-block text-xs text-[#4A5568] border-l border-[#E8DFD1] pl-2 ml-1">
              Jewellery Expert
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Translate size={16} className="text-[#4A5568]" />
            <select
              data-testid="chat-language-select"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="text-sm px-2 py-1.5 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
            >
              {LANGS.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
        data-testid="chat-scroll"
      >
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-6">
          {empty ? (
            <div className="text-center pt-8 pb-6">
              <div className="inline-flex h-14 w-14 rounded-full bg-[#3B5BDB]/10 items-center justify-center">
                <ChatCircleDots size={28} weight="duotone" className="text-[#3B5BDB]" />
              </div>
              <h2 className="font-display text-3xl mt-4 text-[#0A1128]">
                Vanakkam! Ask Mine AI anything.
              </h2>
              <p className="mt-2 text-[#4A5568] text-sm">
                Tamil, Hindi, Telugu, or English — your expert is ready.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-left">
                {SUGGEST.map((s, i) => (
                  <button
                    key={s.q}
                    data-testid={`chat-suggest-${i}`}
                    onClick={() => send(s.q)}
                    className="mc-card lift smooth p-4 flex items-start gap-3 fade-up"
                    style={{ animationDelay: `${i * 60}ms` }}
                  >
                    <s.icon size={20} weight="duotone" className="text-[#3B5BDB] mt-0.5 shrink-0" />
                    <span className="text-sm text-[#0A1128]/90">{s.q}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {messages.map((m) => (
                <MessageBubble key={m.id} message={m} />
              ))}
              {sending && (
                <div className="flex">
                  <div className="bubble-ai px-4 py-3" data-testid="typing-indicator">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="border-t border-[#E8DFD1] bg-white/80 backdrop-blur">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4">
          <div className="flex items-center gap-2 mc-card p-2">
            <button
              data-testid="chat-image-btn"
              onClick={() => navigate("/verify")}
              className="p-2 rounded-md text-[#4A5568] hover:text-[#3B5BDB] hover:bg-[#3B5BDB]/5 smooth"
              title="Verify a hallmark image"
            >
              <ImageIcon size={20} weight="duotone" />
            </button>
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask Mine AI about jewellery..."
              className="flex-1 bg-transparent outline-none text-base px-2 py-2 placeholder:text-[#4A5568]/60"
            />
            <button
              data-testid="chat-send-btn"
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="px-4 py-2.5 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth disabled:opacity-50 flex items-center gap-2"
            >
              <PaperPlaneTilt size={16} weight="fill" />
              <span className="hidden sm:inline">Send</span>
            </button>
          </div>
          <p className="text-[11px] text-[#4A5568]/70 mt-2 text-center">
            Mine AI can occasionally be wrong. For purchases, always verify in
            person. See our{" "}
            <a href="/ai-policy" target="_blank" rel="noreferrer" className="text-[#3B5BDB] underline">
              AI Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  const modelLabel = !isUser && message.model_used ? prettyModel(message.model_used) : null;
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} fade-up`}
      data-testid={`message-${message.role}`}
    >
      {!isUser && (
        <div
          className="h-9 w-9 mr-2 mt-1 shrink-0 rounded-full bg-white border border-[#E8DFD1] flex items-center justify-center overflow-hidden p-1"
          aria-hidden="true"
        >
          <img
            src="/mineai-logo.jpeg"
            alt=""
            className="h-full w-full object-contain"
          />
        </div>
      )}
      <div
        className={`max-w-[85%] sm:max-w-[75%] px-4 py-3 ${
          isUser ? "bubble-user" : "bubble-ai"
        }`}
      >
        <p className="whitespace-pre-wrap text-[15px] leading-relaxed">
          {message.content}
        </p>
        {modelLabel && (
          <p
            className="mt-2 text-[10px] uppercase tracking-wider text-[#4A5568]/70"
            data-testid="message-model-badge"
            title={message.model_used}
          >
            via {modelLabel}
          </p>
        )}
      </div>
    </div>
  );
}

function prettyModel(slug) {
  if (!slug) return null;
  const map = {
    "anthropic/claude-sonnet-4.5": "Claude Sonnet 4.5",
    "anthropic/claude-haiku-4.5": "Claude Haiku 4.5",
    "google/gemini-2.5-flash": "Gemini 2.5 Flash",
    "openai/gpt-4o": "GPT-4o",
    "meta-llama/llama-3.3-70b-instruct:free": "Llama 3.3 70B (free)",
  };
  if (map[slug]) return map[slug];
  if (slug.startsWith("emergent/anthropic")) return "Claude (fallback)";
  if (slug.startsWith("emergent/gemini")) return "Gemini (fallback)";
  // Fallback: last segment, prettified
  return slug.split("/").pop().replace(/-/g, " ");
}
